import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useState } from "react"
import { ScrollGalleryModal } from "./ScrollGalleryModal"
import "./styles.css"

type ScrollGalleryItem = {
  id: string
  label?: string
  coverSrc: string
  coverAlt: string
  coverTitle: string
  coverDimensions: { width?: number; height?: number }
  images: Array<{ src: string; alt: string; title: string; dimensions: { width?: number; height?: number } }>
}

export function ScrollGallery({ items }: { items: ScrollGalleryItem[] }) {
  const galleryItems = items.filter((item) => item.coverSrc)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [viewportRef, emblaApi] = useEmblaCarousel({
    align: "start",
    breakpoints: {
      "(min-width: 600px)": { active: false },
    },
    containScroll: "trimSnaps",
    dragFree: false,
    loop: false,
  })

  const updateCarouselState = useCallback(() => {
    if (!emblaApi) return

    setSelectedIndex(emblaApi.selectedScrollSnap())
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    updateCarouselState()
    emblaApi.on("select", updateCarouselState)
    emblaApi.on("settle", updateCarouselState)
    emblaApi.on("reInit", updateCarouselState)

    return () => {
      emblaApi.off("select", updateCarouselState)
      emblaApi.off("settle", updateCarouselState)
      emblaApi.off("reInit", updateCarouselState)
    }
  }, [emblaApi, updateCarouselState])

  const scrollPrev = () => {
    emblaApi?.scrollPrev()
  }

  const scrollNext = () => {
    emblaApi?.scrollNext()
  }

  const openItem = (index: number) => {
    setOpenIndex(index)
  }

  const closeItem = () => {
    setOpenIndex(null)
  }

  return (
    <div
      className="section-gallery scroll-gallery"
      data-id="scroll-gallery"
      id="scroll-gallery"
    >
      <div
        className="scroll-gallery__viewport"
        ref={viewportRef}
      >
        <ul
          className="scroll-gallery__container"
          data-testid="scroll-gallery-modal"
        >
          {galleryItems.map((item, index) => (
            <li
              className="scroll-gallery__slide"
              data-id={`scroll-gallery-item-${item.id}`}
              key={item.id}
            >
              <button
                className="scroll-gallery__button"
                data-id={`scroll-gallery-open-${item.id}`}
                type="button"
                aria-label={`Open gallery: ${item.label || item.coverAlt || item.coverTitle || item.id}`}
                onClick={() => openItem(index)}
              >
                <img
                  className="scroll-gallery__image"
                  src={item.coverSrc}
                  alt={item.coverAlt}
                  title={item.coverTitle}
                  {...item.coverDimensions}
                />
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div
        className="scroll-gallery__controls"
        aria-label="Marketing gallery controls"
      >
        <button
          className="scroll-gallery__control scroll-gallery__control--prev"
          data-id="scroll-gallery-prev"
          type="button"
          onClick={scrollPrev}
          disabled={!canScrollPrev}
          aria-label="Previous marketing item"
        >
          <i className="fa-sharp fa-solid fa-chevron-left" />
        </button>
        <div
          className="scroll-gallery__status"
          data-id="marketing-gallery-status"
          data-testid="marketing-gallery-status"
          aria-live="polite"
        >
          {galleryItems.length > 0 ? selectedIndex + 1 : 0} / {galleryItems.length}
        </div>
        <button
          className="scroll-gallery__control scroll-gallery__control--next"
          data-id="scroll-gallery-next"
          type="button"
          onClick={scrollNext}
          disabled={!canScrollNext}
          aria-label="Next marketing item"
        >
          <i className="fa-sharp fa-solid fa-chevron-right" />
        </button>
      </div>
      <ScrollGalleryModal
        items={galleryItems}
        openIndex={openIndex}
        onOpenChange={(open) => {
          if (!open) closeItem()
        }}
      />
    </div>
  )
}

export default ScrollGallery
